<script>
  import { page } from '$app/state'
  import { enhance } from '$app/forms';
  import { refreshAll } from '$app/navigation';
  import SearchSelect from '#lib/SearchSelect.svelte';
  import { fetch_json } from "#lib/util.js";


  let { 
    /** @type {function} */
    on_switch_user,
    /** @type {('dropdown'|'inline')} */
    container,
    /** @type {'small' | 'medium' | number | string} */
    width,
   } = $props();

  // let site_settings = getContext('site_settings');
 
  let selected_user_id = $state(page.data.current_user?.user_id);
  let search_term = $state('');
  let form_ref = $state();
  let user_search_results = $state([]);

  // Initial account options from server-loaded data
  let initial_options = $derived.by(() => {
    if(!page.data.authenticated_user) {
      return [];
    }
    
    const authenticated_user = {
      value: page.data.authenticated_user.user_id,
      title: page.data.authenticated_user.name,
      description: page.data.authenticated_user.email,
      image: page.data.authenticated_user.avatar,
    }
    // Filter out authenticated user from other users to prevent duplicates
    const other_users = page.data.account_switcher_users
      .filter(user => user.user_id !== authenticated_user?.value)
      .map(user => ({
        value: user.user_id,
        title: user.name,
        description: user.email,
        image: user.avatar,
      }));
    
    // Return authenticated user first (if exists), followed by other users
    return authenticated_user ? [authenticated_user, ...other_users] : other_users;
  });

  async function search_users(term) {
    try {
      const result = await fetch_json('POST', '/api/search-users', { 
        search_query: term,
        // TODO: Implement pagination or infinite scroll if search results exceed the limit of 8 users
        limit: 8,
        sort_by: 'last_impersonated'
      });
      if (result.total_count > 0 && Array.isArray(result.users)) {
        return result.users.map(user => ({
          value: user.user_id,
          title: user.name,
          description: user.email,
          image: user.avatar,
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }
  
  const account_options = $derived.by(() => {
    if (!search_term) {
      return initial_options;
    }

    // Create a Set of user IDs to track duplicates
    const seen_user_ids = new Set();
    const merged_results = [];

    // Helper to add unique results
    const add_unique_results = (results) => {
      for (const result of results) {
        if (!seen_user_ids.has(result.value)) {
          seen_user_ids.add(result.value);
          merged_results.push(result);
        }
      }
    };

    // First add matching items from initial options (for instant results)
    const term = search_term.toLowerCase();
    const matching_initial = initial_options.filter(option => 
      option.title.toLowerCase().includes(term) ||
      option.description?.toLowerCase().includes(term)
    );
    add_unique_results(matching_initial);

    add_unique_results(user_search_results);

    return merged_results;
  });
</script>

{#if page.data.authenticated_user?.role === 'admin' || account_options.length > 0}
  <form 
    bind:this={form_ref}
    class='account-switcher flex-column' 
    method='POST' 
    action='/login?/switch_user' 
    use:enhance={({ formElement, formData, action, cancel }) => {
      // Add the selected user ID to the form data
      formData.set('delegator', selected_user_id);
      return async ({ result }) => {
        // NOTE: it's important we call on_switch_user not before the result is there, otherwise what happens
        // is that the popover gets closed immediately and then the parent element receives click events
        // e.g. creating an accidental booking. See: https://github.com/michael/system606/issues/1231
        on_switch_user?.();
        // Reload the entire page data to reflect the new user context
        refreshAll();
      };
    }}
  >
    <SearchSelect
      name='delegator'
      allow_null={false}
      option_size='small'
      {container}
      {width}
      options={account_options}
      bind:value={selected_user_id}
      bind:search_term={search_term}
      on_select={({value}) => {
        selected_user_id = value;
        form_ref.requestSubmit();
      }}
      on_search={async (term, callback) => {
        try {
          user_search_results = await search_users(term);
        } finally {
          callback?.();
        }
      }}
      enable_search={page.data.authenticated_user.role === 'admin' || account_options.length > 5}
      disabled={account_options.length === 1 && page.data.authenticated_user.role !== 'admin'}
    />
  </form>
{/if}

<style lang="postcss">
  .account-switcher {
    :global(.select-list) {
      max-height: 250px;
    }
  }
</style>